import { Clear } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/lab';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TextField
} from '@mui/material';
import enLocale from 'date-fns/locale/en-US';
import React, { useEffect, useState } from 'react';
import { typingTimeOut } from '../../constants';
import { ReportPaymentCategories } from '../../enums/reportPaymentCategories';
import { ReportStatus } from '../../enums/reportStatus';
import { useUserRoles } from '../../hooks/useUserRoles';
import { Talent } from '../../models/talent';
import { Company } from '../../models/company';
import { talentService } from '../../services/talentService';
import { companyServices } from '../../services/companyServices';

export interface FilterReportsForm {
  talentId?: Talent[];
  startDate?: string | null;
  endDate?: string | null;
  paymentCategories?: string[];
  companyName?: Company | null;
  status?: string[];
}

interface Props {
  handleFormSubmit: (data: FilterReportsForm) => void;
  filterData: FilterReportsForm;
}

const defaultValues: FilterReportsForm = {
  talentId: [],
  startDate: null,
  endDate: null,
  paymentCategories: [],
  status: [],
  companyName: null
};

const FilterPayments = (props: Props): JSX.Element => {
  const [filterData, setFilterData] = useState<FilterReportsForm>(
    props.filterData
  );
  const [talents, setTalents] = useState<Talent[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);

  const { isAdmin } = useUserRoles();

  useEffect(() => {
    getTalents();
    if (isAdmin) {
      getCompanies();
    }
  }, []);

  const getTalents = () => {
    talentService.getTalentList().then((response) => {
      setTalents(response.data);
    });
  };

  const getCompanies = () => {
    companyServices.getCompaniesAutocomplete().then((response) => {
      setCompanies(response.data);
    });
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      props.handleFormSubmit(filterData);
    }, typingTimeOut);

    return () => clearTimeout(timeoutId);
  }, [filterData]);

  const handleSelectChange = (
    event: SelectChangeEvent<string[]> | SelectChangeEvent
  ) => {
    const {
      target: { value, name }
    } = event;
    setFilterData((prevState) => ({
      ...prevState,
      [name]: value
    }));
  };

  const onClear = () => {
    setFilterData(defaultValues);
  };

  return (
    <form>
      <Grid container>
        <Grid item xs={11}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Autocomplete
              multiple
              id="talent"
              options={talents}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              value={filterData.talentId ?? []}
              onChange={(_event, newValue) => {
                setFilterData((prevState) => ({
                  ...prevState,
                  talentId: newValue
                }));
              }}
              filterSelectedOptions
              sx={{ width: '100%', maxWidth: 245 }}
              renderInput={(params) => <TextField {...params} label="Talent" />}
            />
            {isAdmin && (
              <Autocomplete
                id="companyName"
                options={companies}
                getOptionLabel={(option) => option.name}
                value={filterData.companyName}
                onChange={(_event, newValue) => {
                  setFilterData((prevState) => ({
                    ...prevState,
                    companyName: newValue
                  }));
                }}
                filterSelectedOptions
                sx={{ width: '100%', maxWidth: 245 }}
                renderInput={(params) => (
                  <TextField {...params} label="Company" />
                )}
              />
            )}
            <FormControl sx={{ width: '100%', maxWidth: 245 }}>
              <InputLabel id="category">Payment Category</InputLabel>
              <Select
                labelId="paymentCategories"
                label="paymentCategories"
                multiple
                name="paymentCategories"
                value={filterData.paymentCategories}
                onChange={handleSelectChange}
                input={<OutlinedInput label="Payment Category" />}
                renderValue={(selected: string[]) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} sx={{ height: 'auto' }} />
                    ))}
                  </Box>
                )}
              >
                {Object.keys(ReportPaymentCategories).map((statusKey) => (
                  <MenuItem
                    key={statusKey}
                    value={
                      ReportPaymentCategories[
                        statusKey as keyof typeof ReportPaymentCategories
                      ]
                    }
                  >
                    {
                      ReportPaymentCategories[
                        statusKey as keyof typeof ReportPaymentCategories
                      ]
                    }
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl sx={{ width: '100%', maxWidth: 245 }}>
              <InputLabel id="status">Status</InputLabel>
              <Select
                labelId="status"
                label="status"
                multiple
                name="status"
                value={filterData.status}
                onChange={handleSelectChange}
                input={<OutlinedInput label="status" />}
                renderValue={(selected: string[]) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip key={value} label={value} sx={{ height: 'auto' }} />
                    ))}
                  </Box>
                )}
              >
                {Object.keys(ReportStatus).map((statusKey) => (
                  <MenuItem
                    key={statusKey}
                    value={ReportStatus[statusKey as keyof typeof ReportStatus]}
                  >
                    {ReportStatus[statusKey as keyof typeof ReportStatus]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={enLocale}
            >
              <DatePicker
                label="Start Date"
                onChange={(date) => {
                  if (date)
                    setFilterData((prevState) => ({
                      ...prevState,
                      startDate: date
                    }));
                }}
                value={filterData.startDate}
                renderInput={(params) => (
                  <TextField sx={{ maxWidth: 245 }} {...params} />
                )}
              />
            </LocalizationProvider>
            <LocalizationProvider
              dateAdapter={AdapterDateFns}
              locale={enLocale}
            >
              <DatePicker
                label="End Date"
                onChange={(date) => {
                  if (date)
                    setFilterData((prevState) => ({
                      ...prevState,
                      endDate: date
                    }));
                }}
                value={filterData.endDate}
                renderInput={(params) => (
                  <TextField sx={{ maxWidth: 245 }} {...params} />
                )}
              />
            </LocalizationProvider>
          </Box>
        </Grid>
        <Grid item xs={1}>
          <Button
            variant="text"
            startIcon={<Clear />}
            onClick={() => onClear()}
          >
            Clear
          </Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default FilterPayments;
